/// <reference lib="webworker" />

/**
 * Client-side Whisper worker — the browser half of hybrid transcription.
 *
 * Receives 16kHz mono Float32Array audio, runs whisper-tiny.en (quantized,
 * ~40MB one-time download cached by the browser) on WebGPU when available or
 * WASM otherwise, and streams progress back. Same 30s windowing + segment
 * shape as the server pipeline so the saved result is indistinguishable.
 */

type InMsg = {
  audio: Float32Array;
  devices: Array<"webgpu" | "wasm">;
  /** Where this slice starts in the ORIGINAL recording, for timestamp offsets. */
  offsetSeconds?: number;
};

/**
 * The pipeline is cached across messages so a sliced long call loads the model
 * ONCE. Rebuilding it per slice would re-pay the ~30-60MB init on every chunk
 * and defeat the point of slicing.
 */
let cachedAsr: any = null;
let cachedKey = "";

/**
 * MODEL BY BACKEND (2026-08-21). base.en is ~2x tiny.en's size (~60MB vs ~30MB)
 * and materially more accurate on 8kHz telephony audio, which is all we ever
 * feed it — that is exactly the regime where tiny.en degrades worst.
 *
 * But it is only worth it where there is a GPU: the machines that fall back to
 * WASM are the slow ones, and doubling model size is precisely the wrong thing
 * to do to them. A GPU adapter is a good proxy for "this machine can afford it",
 * and the caller already probes for one.
 */
export const MODEL_FOR_DEVICE = {
  webgpu: "onnx-community/whisper-base.en",
  wasm: "onnx-community/whisper-tiny.en",
} as const;

function buildAsr(device: "webgpu" | "wasm") {
  return (async () => {
    const { pipeline } = await import("@huggingface/transformers");
    return pipeline("automatic-speech-recognition", MODEL_FOR_DEVICE[device], {
      // q8 decoder trips a MatMulNBits qdq bug in the web runtime — fp32
      // encoder + q4 decoder is the known-good browser combo. Verified
      // 2026-08-21 that BOTH models publish decoder_model_merged_q4.onnx.
      // Size depends on the model above: tiny.en ~30MB, base.en ~60MB.
      dtype: { encoder_model: "fp32", decoder_model_merged: "q4" },
      device,
      progress_callback: (p: any) => {
        if (p?.status === "progress" && p?.total) {
          (self as unknown as Worker).postMessage({
            type: "model",
            pct: Math.min(100, Math.round((p.loaded / p.total) * 100)),
          });
        }
      },
    } as any);
  })();
}

/** Try devices in order — a machine that advertises WebGPU can still fail to
 *  init a backend (no adapter, headless, driver quirks); WASM is the net. */
async function getAsr(devices: Array<"webgpu" | "wasm">) {
  const key = devices.join(",");
  if (cachedAsr && cachedKey === key) return { asr: cachedAsr, device: devices[0] };
  let lastErr: any = null;
  for (const device of devices) {
    try {
      const asr = await buildAsr(device);
      cachedAsr = asr;
      cachedKey = key;
      return { asr, device };
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr || new Error("No usable backend");
}

self.onmessage = async (e: MessageEvent<InMsg>) => {
  const { audio, devices, offsetSeconds = 0 } = e.data;
  const post = (m: any) => (self as unknown as Worker).postMessage(m);
  try {
    const { asr, device: usedDevice } = await getAsr(devices?.length ? devices : ["wasm"]);
    post({ type: "model-selected", model: MODEL_FOR_DEVICE[usedDevice], device: usedDevice });
    const WINDOW = 30 * 16000;
    const segments: Array<{ start: number; end: number; text: string }> = [];
    for (let offset = 0; offset < audio.length; offset += WINDOW) {
      const slice = audio.subarray(offset, Math.min(audio.length, offset + WINDOW));
      const windowStart = offsetSeconds + offset / 16000;
      const result: any = await asr(slice, { return_timestamps: true });
      for (const c of result?.chunks || []) {
        const text = String(c?.text || "").trim();
        if (!text) continue;
        const s0 = Math.max(0, Number(c.timestamp?.[0] ?? 0)) + windowStart;
        const rawEnd = Number(c.timestamp?.[1]);
        const s1 = Number.isFinite(rawEnd) ? rawEnd + windowStart : windowStart + slice.length / 16000;
        segments.push({ start: Math.round(s0 * 10) / 10, end: Math.round(s1 * 10) / 10, text });
      }
      post({ type: "progress", processedSeconds: Math.min(audio.length, offset + WINDOW) / 16000 });
    }
    post({ type: "done", segments });
  } catch (err: any) {
    post({ type: "error", message: err?.message || "Transcription failed" });
  }
};
